import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './EventModal.css';
import CloseIcon from '@mui/icons-material/Close';
import MovieIcon from '@mui/icons-material/Movie';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PhoneIcon from '@mui/icons-material/Phone';

function EventModal({ event, onClose }) {
  const navigate = useNavigate();

  useEffect(() => {
    // Prevent background scrolling when modal is open
    document.body.style.overflow = 'hidden';
    
    // Push a new history state when modal opens
    window.history.pushState({ modal: true }, '');
    
    // Handle back button/gesture
    const handlePopState = (e) => {
      onClose();
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      document.body.style.overflow = 'unset';
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onClose]);

  const handleRegisterClick = () => {
    // Close modal and navigate
    onClose();
    // Use setTimeout to ensure modal closes before navigation
    setTimeout(() => {
      navigate('/register', { state: { event } });
    }, 0);
  };
  
  const handleClose = () => {
    // Go back in history to remove the modal state
    if (window.history.state?.modal) {
      window.history.back();
    } else {
      onClose();
    }
  };

  if (!event) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={handleClose}><CloseIcon /></button>
        
        <div className="modal-hero" style={{ backgroundImage: `url(${event.image})` }}>
          <div className="modal-hero-gradient"></div>
          <div className="modal-hero-content">
            <h1 className="modal-title">{event.title}</h1>
            <div className="modal-meta">
              <span className="modal-tag">{event.category}</span>
              {event.netflixTheme && <span className="modal-tag"><MovieIcon fontSize="small" /> {event.netflixTheme}</span>}
              {event.entryType && <span className="modal-tag">{event.entryType}</span>}
            </div>
          </div>
        </div>

        <div className="modal-body">
          <div className="modal-info">
            <div className="modal-actions">
              <button className="modal-btn modal-btn-play" onClick={handleRegisterClick}>
                <PlayArrowIcon /> Register Now
              </button>
            </div>

            <div className="modal-details">
              <div className="modal-detail-row">
                <span className="modal-label">Category:</span>
                <span className="modal-value">{event.category}</span>
              </div>
              <div className="modal-detail-row">
                <span className="modal-label">Date:</span>
                <span className="modal-value">{event.date}</span>
              </div>
              <div className="modal-detail-row">
                <span className="modal-label">Time:</span>
                <span className="modal-value">{event.time}</span>
              </div>
              <div className="modal-detail-row">
                <span className="modal-label">Venue:</span>
                <span className="modal-value">
                  {Array.isArray(event.venue) ? event.venue.join(', ') : event.venue}
                </span>
              </div>
              {event.entryFee && (
                <div className="modal-detail-row">
                  <span className="modal-label">Entry Fee:</span>
                  <span className="modal-value">{event.entryFee}</span>
                </div>
              )}
              {event.structure && (
                <>
                  {event.structure.total_slots && (
                    <div className="modal-detail-row">
                      <span className="modal-label">Total Slots:</span>
                      <span className="modal-value">{event.structure.total_slots}</span>
                    </div>
                  )}
                  {event.structure.players_per_team && (
                    <div className="modal-detail-row">
                      <span className="modal-label">Team Size:</span>
                      <span className="modal-value">{event.structure.players_per_team}</span>
                    </div>
                  )}
                </>
              )}
              {event.format && (
                <div className="modal-detail-row">
                  <span className="modal-label">Format:</span>
                  <span className="modal-value">{event.format}</span>
                </div>
              )}
            </div>

            <p className="modal-description">{event.description}</p>

            <div className="modal-section">
              <h3 className="modal-section-title">Cast</h3>
              <div className="modal-cast">
                <span className="modal-label">Event Coordinators:</span>
                <div className="modal-cast-list">
                  {event.coordinators.map((coordinator, index) => (
                    <div key={index} className="coordinator-item">
                      <div className="coordinator-name">{coordinator}</div>
                      {event.coordinatorPhones && event.coordinatorPhones[index] && (
                        <div className="coordinator-phone"><PhoneIcon fontSize="small" /> {event.coordinatorPhones[index]}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {event.rounds && event.rounds.length > 0 && (
              <div className="modal-section">
                <h3 className="modal-section-title">Episodes</h3>
                <div className="modal-episodes">
                  {event.rounds.map((round, index) => (
                    <div key={index} className="modal-episode">
                      <div className="episode-number">{index + 1}</div>
                      <div className="episode-info">
                        <h4 className="episode-title">{round.name || `Round ${index + 1}`}</h4>
                        {round.focus && <p className="episode-desc">Focus: {round.focus}</p>}
                        {round.task && <p className="episode-desc">Task: {round.task}</p>}
                        {round.format && <p className="episode-desc">Format: {round.format}</p>}
                        {round.duration && <p className="episode-desc">Duration: {round.duration}</p>}
                        {round.goal && <p className="episode-desc">Goal: {round.goal}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {event.categories && event.categories.length > 0 && (
              <div className="modal-section">
                <h3 className="modal-section-title">Categories</h3>
                <div className="modal-episodes">
                  {event.categories.map((cat, index) => (
                    <div key={index} className="modal-episode">
                      <div className="episode-number">{index + 1}</div>
                      <div className="episode-info">
                        <h4 className="episode-title">{cat.type}</h4>
                        {cat.time_limit && <p className="episode-desc">Time Limit: {cat.time_limit}</p>}
                        {cat.fee && <p className="episode-desc">Fee: ₹{cat.fee}</p>}
                        {cat.max_members && <p className="episode-desc">Max Members: {cat.max_members}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {event.rules && event.rules.length > 0 && (
              <div className="modal-section">
                <h3 className="modal-section-title">Disclaimer</h3>
                <ul className="modal-rules">
                  {event.rules.map((rule, index) => (
                    <li key={index}>{rule}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventModal;
